import React from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import {
  CloudShowersHeavyRegularIcon,
  CloudSunRainRegularIcon,
  SnowflakeRegularIcon,
  SunBrightRegularIcon
} from '../../shared/icons/icons';

import { GET_ZIP_CODE_WEATHER_DATA } from '../../shared/graphql/form-queries';

import {
  useMyStore,
  useQuery,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { isValid5DigitPostalCode } from '../../shared/utils/validators/validators';

import { Maybe } from '../../shared/types/graphql-types';

import './weather-conditions.scss';

const DEFAULT_WET_DISTANCE_ZIP_CODE = '67205';

interface WeatherConditionsProps {
  className?: string;
  precipitationDaysPerYear?: Maybe<number>;
  title?: Maybe<string>;
  tooltip?: Maybe<string>;
  winterDaysPerYear?: Maybe<number>;
}

function WeatherConditions(props: WeatherConditionsProps) {
  const {
    className,
    precipitationDaysPerYear: precipitationDaysPerYearProp,
    title = 'Weather Conditions',
    tooltip,
    winterDaysPerYear: winterDaysPerYearProp
  } = props || {};
  const { myStore } = useMyStore();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { zipCodeValue: treadwellZipCode } = treadwellPreferences || {};

  const zipCode =
    treadwellZipCode ||
    myStore?.address?.postalCode?.replace(/[^0-9].+$/, '') ||
    DEFAULT_WET_DISTANCE_ZIP_CODE;

  const {
    data: weatherData,
    error: weatherDataError,
    isLoading: weatherDataLoading
  } = useQuery(GET_ZIP_CODE_WEATHER_DATA, {
    skip:
      !isValid5DigitPostalCode(zipCode) ||
      (Number.isFinite(precipitationDaysPerYearProp) &&
        Number.isFinite(winterDaysPerYearProp)),
    variables: {
      zipCode
    }
  });

  const {
    precipitationDaysPerYear = precipitationDaysPerYearProp,
    winterDaysPerYear = winterDaysPerYearProp
  } =
    weatherDataLoading || weatherDataError || !weatherData
      ? {}
      : weatherData?.recommendationQueries?.weatherDetails || {};
  const dryDaysPerYear = 365 - (precipitationDaysPerYear || 0);

  return (
    <div className={className} styleName="container">
      <div styleName="title">
        <CloudSunRainRegularIcon />
        <Typography styleName="title-text" weight="heavy">
          {title}
        </Typography>{' '}
        {tooltip && <Tooltip content={tooltip} />}
      </div>
      <div styleName="metrics-container">
        {Boolean(precipitationDaysPerYear) && (
          <div data-testid="precipitation-days-container">
            <CloudShowersHeavyRegularIcon styleName="metric-icon" />
            {precipitationDaysPerYear} avg. days
          </div>
        )}
        {Boolean(winterDaysPerYear) && (
          <div data-testid="winter-days-container">
            <SnowflakeRegularIcon styleName="metric-icon" /> {winterDaysPerYear}{' '}
            avg. days
          </div>
        )}
        {Boolean(dryDaysPerYear) && (
          <div data-testid="dry-days-container">
            <SunBrightRegularIcon styleName="metric-icon" />
            {dryDaysPerYear} avg. days in {zipCode}
          </div>
        )}
      </div>
    </div>
  );
}

export default WeatherConditions;
